import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api, fmtUSD } from "../lib/api";
import "./RevenueIntelligence.css";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, LineChart, Line, ReferenceLine, Cell,
} from "recharts";
import { Input } from "../components/ui/input";
import { Search, CheckCircle2, AlertTriangle, TrendingUp, Banknote } from "lucide-react";

/* ------------ 3 streamlined tabs (was 7) ------------ */
const TABS = [
  { id: "reconciliation", label: "Bank ↔ CTMS Reconciliation" },
  { id: "performance", label: "Performance & Forecast" },
  { id: "ar", label: "AR & Collections", className: "ar-tab" },
];

const fmtK = (v) => v == null ? "—" : `$${Math.round(v / 1000)}K`;
const fmtM = (v) => v == null ? "—" : `$${(v / 1000000).toFixed(2)}M`;
const fmtUSDcents = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RevenueIntelligence() {
  const [tab, setTab] = useState("reconciliation");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const needed = {
      reconciliation: ["reconciliation", "historical"],
      performance: ["overview", "tva"],
      ar: ["ar", "split"],
    }[tab] || [];
    const endpoints = {
      reconciliation: "/revenue/reconciliation",
      historical: "/revenue/historical",
      overview: "/revenue/overview",
      tva: "/revenue/target-vs-actual",
      ar: "/revenue/ar-tracker",
      split: "/revenue/split",
    };
    const missing = needed.filter((k) => !data[k]);
    if (missing.length === 0) return;
    setLoading(true);
    Promise.all(missing.map((k) => api.get(endpoints[k]).then((r) => [k, r.data])))
      .then((pairs) => setData((p) => ({ ...p, ...Object.fromEntries(pairs) })))
      .finally(() => setLoading(false));
  }, [tab, data]);

  return (
    <AppShell
      title="Revenue Intelligence"
      subtitle="Bank ↔ CTMS reconciliation · Performance · AR collections"
    >
      <div className="ri">
        <div className="ritabs" role="tablist" data-testid="ri-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`ri-tab-${t.id}`}
              className={`ritab ${t.className || ""} ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>}

        {tab === "reconciliation" && data.reconciliation && (
          <ReconciliationTab recon={data.reconciliation} hist={data.historical} />
        )}
        {tab === "performance" && data.overview && data.tva && (
          <PerformanceTab overview={data.overview} tva={data.tva} />
        )}
        {tab === "ar" && data.ar && data.split && (
          <ARTab ar={data.ar} split={data.split} />
        )}
      </div>
    </AppShell>
  );
}

/* ============================================================
 * RECONCILIATION TAB (hero — bank vs CTMS by source/month)
 * ============================================================ */
function ReconciliationTab({ recon, hist }) {
  const k = recon.kpis;
  const matched = (k.ytd_unmatched || 0) === 0;

  return (
    <>
      <div className="sec">
        <div className="sec-t">Bank ↔ CTMS Reconciliation · 2026 YTD</div>
        <div className="sec-l" />
        <div className={`tag ${matched ? "green" : "orange"}`}>
          {matched ? "All matched" : `${fmtUSD(k.ytd_unmatched)} unmatched`}
        </div>
      </div>

      <div className="krow k4" data-testid="ri-recon-kpis">
        <KCard
          color="green"
          icon={<Banknote size={18} />}
          label="Bank YTD Received"
          value={fmtM(k.ytd_bank)}
          sub="Confirmed in bank · ground truth"
          pill="Jan–Apr 2026"
          pillClass="good"
        />
        <KCard
          color="blue"
          icon={<TrendingUp size={18} />}
          label="CTMS YTD Tracked"
          value={fmtM(k.ytd_ctms)}
          sub="CRIO + eClinPro reported"
          pill="System data"
          pillClass="info"
        />
        <KCard
          color={matched ? "green" : "orange"}
          icon={<CheckCircle2 size={18} />}
          label="Match Rate"
          value={`${k.match_rate_pct}%`}
          sub={matched ? "Bank = CTMS · clean books" : "Some entries unmatched"}
          pill={matched ? "Reconciled" : "Investigate"}
          pillClass={matched ? "good" : "warn"}
        />
        <KCard
          color={k.ytd_unmatched > 0 ? "red" : "green"}
          icon={<AlertTriangle size={18} />}
          label="Unmatched $"
          value={k.ytd_unmatched ? fmtUSD(k.ytd_unmatched) : "$0"}
          valueColor={k.ytd_unmatched > 0 ? "red" : "green"}
          sub={k.ytd_unmatched ? "Diff between bank & CTMS" : "No discrepancy"}
          pill="Bank vs CTMS"
          pillClass={k.ytd_unmatched > 0 ? "danger" : "good"}
        />
      </div>

      {/* YTD by source — small table mimicking the right-side of the screenshot */}
      <div className="grid2">
        <Card title="YTD By Source" sub="2026 cumulative · CRIO + eClinPro" tag="By system" tagColor="blue">
          <table className="recon-table" data-testid="ri-recon-ytd">
            <thead>
              <tr>
                <th>System</th>
                <th className="right">Bank</th>
                <th className="right">CTMS</th>
                <th className="right">Diff</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {recon.ytd.sources.map((s) => (
                <tr key={s.system}>
                  <td><span className={`sp ${s.system === "eClinPro" ? "sp-ec" : "sp-crio"}`}>{s.system}</span></td>
                  <td className="m g">{fmtUSDcents(s.bank)}</td>
                  <td className="m b">{fmtUSDcents(s.ctms)}</td>
                  <td className={`m ${Math.abs(s.diff) < 1 ? "" : "r"}`}>
                    {Math.abs(s.diff) < 1 ? "—" : fmtUSDcents(s.diff)}
                  </td>
                  <td style={{ fontSize: 11, color: "#6B7280" }}>{s.notes || "—"}</td>
                </tr>
              ))}
              <tr style={{ background: "#F9FAFB", fontWeight: 800 }}>
                <td>TOTAL</td>
                <td className="m g"><strong>{fmtUSDcents(recon.ytd.bank_total)}</strong></td>
                <td className="m b"><strong>{fmtUSDcents(recon.ytd.ctms_total)}</strong></td>
                <td className={`m ${Math.abs(recon.ytd.diff_total) < 1 ? "" : "r"}`}>
                  <strong>{Math.abs(recon.ytd.diff_total) < 1 ? "—" : fmtUSDcents(recon.ytd.diff_total)}</strong>
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </Card>

        <Card title="Bank vs CTMS · Monthly" sub="Should overlay perfectly when reconciled" tag="2026" tagColor="green">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={recon.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="bank_total" name="Bank" fill="#16A34A" radius={[3, 3, 0, 0]} />
              <Bar dataKey="ctms_total" name="CTMS" fill="#0A4C95" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Hero matrix: month × source */}
      <Card
        title="Monthly Reconciliation Matrix"
        sub="Bank deposits vs CTMS-tracked revenue · split by CRIO & eClinPro"
        tag="Drill-down"
        tagColor="blue"
        full
      >
        <table className="recon-table recon-matrix" data-testid="ri-recon-matrix">
          <thead>
            <tr>
              <th rowSpan={2}>Month</th>
              <th colSpan={3} className="grp grp-ec">eClinPro</th>
              <th colSpan={3} className="grp grp-crio">CRIO</th>
              <th colSpan={3} className="grp grp-tot">Total</th>
              <th rowSpan={2}>Notes</th>
            </tr>
            <tr>
              <th className="right grp-ec">Bank</th>
              <th className="right grp-ec">CTMS</th>
              <th className="right grp-ec">Diff</th>
              <th className="right grp-crio">Bank</th>
              <th className="right grp-crio">CTMS</th>
              <th className="right grp-crio">Diff</th>
              <th className="right grp-tot">Bank</th>
              <th className="right grp-tot">CTMS</th>
              <th className="right grp-tot">Diff</th>
            </tr>
          </thead>
          <tbody>
            {recon.monthly.map((m) => {
              const ec = m.sources.find((s) => s.system === "eClinPro") || { bank: 0, ctms: 0 };
              const cr = m.sources.find((s) => s.system === "CRIO") || { bank: 0, ctms: 0 };
              const ecDiff = (ec.bank || 0) - (ec.ctms || 0);
              const crDiff = (cr.bank || 0) - (cr.ctms || 0);
              const flagged = Math.abs(m.diff_total) >= 1;
              return (
                <tr key={m.month} className={flagged ? "flag-row" : ""}>
                  <td><strong>{m.month}</strong></td>
                  <td className="m g">{fmtUSDcents(ec.bank)}</td>
                  <td className="m b">{fmtUSDcents(ec.ctms)}</td>
                  <td className={`m ${Math.abs(ecDiff) < 1 ? "" : "r"}`}>{Math.abs(ecDiff) < 1 ? "—" : fmtUSDcents(ecDiff)}</td>
                  <td className="m g">{fmtUSDcents(cr.bank)}</td>
                  <td className="m b">{fmtUSDcents(cr.ctms)}</td>
                  <td className={`m ${Math.abs(crDiff) < 1 ? "" : "r"}`}>{Math.abs(crDiff) < 1 ? "—" : fmtUSDcents(crDiff)}</td>
                  <td className="m g"><strong>{fmtUSDcents(m.bank_total)}</strong></td>
                  <td className="m b"><strong>{fmtUSDcents(m.ctms_total)}</strong></td>
                  <td className={`m ${flagged ? "r" : ""}`}><strong>{flagged ? fmtUSDcents(m.diff_total) : "—"}</strong></td>
                  <td style={{ fontSize: 11, color: flagged ? "#DC2626" : "#6B7280" }}>{m.notes || "—"}</td>
                </tr>
              );
            })}
            <tr style={{ background: "#F0FDF4", fontWeight: 800, borderTop: "2px solid #16A34A" }}>
              <td>YTD</td>
              <td className="m g">{fmtUSDcents(recon.ytd.sources.find((s) => s.system === "eClinPro").bank)}</td>
              <td className="m b">{fmtUSDcents(recon.ytd.sources.find((s) => s.system === "eClinPro").ctms)}</td>
              <td className="m">—</td>
              <td className="m g">{fmtUSDcents(recon.ytd.sources.find((s) => s.system === "CRIO").bank)}</td>
              <td className="m b">{fmtUSDcents(recon.ytd.sources.find((s) => s.system === "CRIO").ctms)}</td>
              <td className="m r">{fmtUSDcents(recon.ytd.sources.find((s) => s.system === "CRIO").diff)}</td>
              <td className="m g"><strong>{fmtUSDcents(recon.ytd.bank_total)}</strong></td>
              <td className="m b"><strong>{fmtUSDcents(recon.ytd.ctms_total)}</strong></td>
              <td className="m r"><strong>{fmtUSDcents(recon.ytd.diff_total)}</strong></td>
              <td style={{ fontSize: 11, color: "#6B7280" }}>HS study (CRIO untagged)</td>
            </tr>
          </tbody>
        </table>
        <div className="data-note" style={{ marginTop: 12 }} data-testid="ri-recon-note">
          <strong>How this works:</strong> {recon.data_note}
          {" "}Diffs &gt;$1 are flagged — open them in CRIO/eClinPro to find the missing entry.
        </div>
      </Card>

      {hist && (
        <Card title="Trailing 12-Month Trend · Bank vs CTMS Sources" sub="Bank actual (when available) overlaid with eClinPro & CRIO paid dates" full>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={hist.monthly.map((m) => ({
              month: m.month.split(" ")[0] + " '" + m.month.split(" ")[1].slice(2),
              bank: m.bank || null,
              eclinpro: m.eclinpro_paid || 0,
              crio: m.crio_paid || 0,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="bank" name="Bank Actual" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="eclinpro" name="eClinPro" stroke="#0A4C95" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="crio" name="CRIO" stroke="#F05D23" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </>
  );
}

/* ============================================================
 * PERFORMANCE TAB (combines old Overview + Target vs Actual + Forecast)
 * ============================================================ */
function PerformanceTab({ overview, tva }) {
  const k = overview.kpis;
  const monthlyFull = overview.monthly_full;
  const banked = monthlyFull.filter((m) => m.bank != null);
  const tk = tva.kpis;
  const ytdProgressPct = Math.min(100, (k.received_in_bank_ytd / k.ytd_target) * 100);

  return (
    <>
      <div className="sec">
        <div className="sec-t">2026 Performance · Bank Actuals vs $500K/mo Target</div>
        <div className="sec-l" />
        <div className="tag green">Ground truth: bank receipts</div>
      </div>

      <div className="krow k4">
        <KCard color="green" icon={<Banknote size={18} />} label="YTD Bank Received" value={k.received_in_bank_ytd_label}
          sub={`${banked.length} months banked · avg ${fmtK(k.ytd_avg_per_month)}/mo`} pill="On the books" pillClass="good" />
        <KCard color="blue" icon={<TrendingUp size={18} />} label="Best Month" value={fmtK(tk.best_month.value)}
          sub={`${tk.best_month.label} · ${tk.best_month.pct_of_target}% of target`} pill="Closest yet" pillClass="info" />
        <KCard color="orange" icon="🎯" label="Monthly Target" value="$500K"
          sub={`Gap to YTD target: ${fmtK(k.ytd_gap_to_target)}`} pill={`${ytdProgressPct.toFixed(0)}% of YTD plan`} pillClass="warn" />
        <KCard color="purple" icon="📈" label="Apr Projection" value={k.april_projection_label}
          sub={`${fmtK(k.april_banked)} banked + forecasted`} pill="In progress" pillClass="purple" />
      </div>

      <div className="ytd-progress" data-testid="ytd-progress">
        <div className="ytd-progress-track">
          <div className="ytd-progress-fill" style={{ width: `${ytdProgressPct}%` }} />
          <div className="ytd-progress-target" style={{ left: `100%` }} />
        </div>
        <div className="ytd-progress-labels">
          <div><strong>{fmtUSD(k.received_in_bank_ytd)}</strong> banked YTD</div>
          <div style={{ color: "#6B7280" }}>vs {fmtUSD(k.ytd_target)} YTD target</div>
        </div>
      </div>

      <Card title="Monthly Bank Receipts · Actual + Forecast vs $500K Target" sub="Green = bank actual · Blue = visit + AR forecast · Orange line = $500K target" full>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={tva.monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} />
            <Tooltip formatter={(v) => fmtUSD(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={500000} stroke="#F05D23" strokeDasharray="4 4" label={{ value: "$500K target", fontSize: 10, fill: "#F05D23" }} />
            <Bar dataKey="bank" name="Bank Actual" fill="#16A34A" radius={[3, 3, 0, 0]} />
            <Bar dataKey="visit_fc" name="Visit Forecast" stackId="fc" fill="#0E7C7B" radius={[0, 0, 0, 0]} />
            <Bar dataKey="ar_fc" name="AR Collection Forecast" stackId="fc" fill="#0A4C95" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Month-by-Month Detail" sub="Actuals (Jan–Apr) + 6-month forward forecast" full>
        <div className="tva-row tva-head">
          <div>Month</div>
          <div className="right" style={{ background: "#DCFCE7", color: "#16A34A" }}>Bank Actual</div>
          <div className="right" style={{ background: "#E0F2F1", color: "#0E7C7B" }}>Visit FC</div>
          <div className="right" style={{ background: "#E8F1FB", color: "#0A4C95" }}>AR FC</div>
          <div className="right">Total Forecast</div>
          <div className="center">vs $500K</div>
          <div className="center">Status</div>
        </div>
        {tva.monthly.map((m) => (
          <div key={m.month} className="tva-row" style={{ background: m.status === "in_progress" ? "rgba(254,243,199,0.5)" : m.status === "forecast" ? "#F9FAFB" : "white" }}>
            <div><strong>{m.month}</strong></div>
            <div className="right num" style={{ color: "#16A34A", fontWeight: 700 }}>{m.bank ? fmtUSD(m.bank) : "—"}</div>
            <div className="right num" style={{ color: "#0E7C7B", fontWeight: 700 }}>{fmtUSD(m.visit_fc)}</div>
            <div className="right num" style={{ color: "#0A4C95", fontWeight: 700 }}>{fmtUSD(m.ar_fc)}</div>
            <div className="right num" style={{ fontWeight: 700 }}>{fmtUSD(m.forecast_total)}</div>
            <div className="center num" style={{ color: m.diff >= 0 ? "#16A34A" : "#DC2626", fontWeight: 700 }}>
              {m.diff >= 0 ? "+" : ""}{fmtK(m.diff)}
            </div>
            <div className="center"><StatusPill status={m.status} /></div>
          </div>
        ))}
      </Card>
    </>
  );
}

/* ============================================================
 * AR & COLLECTIONS TAB (combines old AR Tracker + Invoice Log + Split)
 * ============================================================ */
function ARTab({ ar, split }) {
  const a = ar.ar_breakdown;
  const [crioQ, setCrioQ] = useState("");
  const [ecQ, setEcQ] = useState("");

  const crioFiltered = useMemo(() => {
    const q = crioQ.toLowerCase();
    return ar.crio_outstanding.filter((r) => !q || r.study.toLowerCase().includes(q) || r.site.toLowerCase().includes(q));
  }, [ar.crio_outstanding, crioQ]);

  const ecFiltered = useMemo(() => {
    const q = ecQ.toLowerCase();
    return ar.eclinpro_outstanding.filter((r) => !q || r.sponsor.toLowerCase().includes(q) || r.study.toLowerCase().includes(q));
  }, [ar.eclinpro_outstanding, ecQ]);

  const total = a.total;

  return (
    <>
      <div className="sec">
        <div className="sec-t">Accounts Receivable · Outstanding by System</div>
        <div className="sec-l" />
        <div className="tag red">{fmtUSD(total)} · {a.total_items} open items</div>
      </div>

      <div className="krow k4">
        <KCard color="red" icon={<AlertTriangle size={18} />} label="Total Outstanding AR" value={fmtK(total)} valueColor="red"
          sub="eClinPro + CRIO + Holdback" pill={`${a.total_items} items`} pillClass="danger" />
        <KCard color="teal" icon="🏥" label="Visit AR" value={fmtK(a.visit_ar)} valueColor="teal"
          sub="Auto-pay items not yet received" pill={`${Math.round(a.visit_ar / total * 100)}% of AR`} pillClass="teal" />
        <KCard color="orange" icon="📄" label="Invoicable AR" value={fmtK(a.invoicable_ar)} valueColor="orange"
          sub="Needs manual invoicing / follow-up" pill={`${Math.round(a.invoicable_ar / total * 100)}% of AR`} pillClass="warn" />
        <KCard color="purple" icon="🔒" label="Holdback (Closeout)" value={fmtUSD(a.holdback)}
          sub="Released at study closeout" pill="CRIO holdback" pillClass="purple" />
      </div>

      {/* AR by source/type strip */}
      <div className="strip red" data-testid="ar-source-strip">
        <div className="si"><div className="sl">eClinPro Visit AR</div><div className="sv">{fmtUSD(a.eclinpro_visit_ar)}</div></div>
        <div className="si"><div className="sl">eClinPro Invoicable</div><div className="sv">{fmtUSD(a.eclinpro_invoicable_ar)}</div></div>
        <div className="sdiv" />
        <div className="si"><div className="sl">CRIO Visit AR</div><div className="sv">{fmtUSD(a.crio_visit_ar)}</div></div>
        <div className="si"><div className="sl">CRIO Invoicable</div><div className="sv">{fmtUSD(a.crio_invoicable_ar)}</div></div>
        <div className="sdiv" />
        <div className="si"><div className="sl">60-day Forecast</div><div className="sv green">$327K expected</div></div>
      </div>

      {/* Collection rates by source × type — moved up from the old Split tab */}
      <Card title="Collection Performance by System & Type" sub="All-time · both CTMS · what's the leakage?" tag={`${(((split?.detail?.reduce((s, r) => s + r.collected, 0) || 0) / (split?.detail?.reduce((s, r) => s + r.total, 0) || 1)) * 100).toFixed(1)}% blended`} tagColor="blue" full>
        <table data-testid="ri-collection-table">
          <thead><tr><th>Type</th><th>System</th><th>Items</th><th className="right">Total Revenue</th><th className="right">Collected</th><th className="right">Outstanding</th><th className="right">Collection %</th></tr></thead>
          <tbody>
            {split.detail.map((r, i) => (
              <tr key={`${r.type}-${r.system}-${i}`}>
                <td><span className={`sp ${r.type.includes("Visit") ? "sp-visit" : "sp-inv"}`}>{r.type}</span></td>
                <td><span className={`sp ${r.system === "eClinPro" ? "sp-ec" : "sp-crio"}`}>{r.system}</span></td>
                <td>{r.items}</td>
                <td className="m b">{fmtUSD(r.total)}</td>
                <td className="m g">{fmtUSD(r.collected)}</td>
                <td className="m r">{fmtUSD(r.outstanding)}</td>
                <td className="m" style={{ color: r.collection_pct >= 90 ? "#16A34A" : r.collection_pct >= 75 ? "#D97706" : "#DC2626", fontWeight: 700 }}>
                  {r.collection_pct.toFixed(1)}%
                </td>
              </tr>
            ))}
            <tr style={{ background: "#F9FAFB", fontWeight: 700 }}>
              <td colSpan={2}>TOTAL</td>
              <td>{split.detail.reduce((s, r) => s + r.items, 0)}</td>
              <td className="m b"><strong>{fmtUSD(split.detail.reduce((s, r) => s + r.total, 0))}</strong></td>
              <td className="m g"><strong>{fmtUSD(split.detail.reduce((s, r) => s + r.collected, 0))}</strong></td>
              <td className="m r"><strong>{fmtUSD(split.detail.reduce((s, r) => s + r.outstanding, 0))}</strong></td>
              <td className="m"><strong>{(((split.detail.reduce((s, r) => s + r.collected, 0)) / (split.detail.reduce((s, r) => s + r.total, 0))) * 100).toFixed(1)}%</strong></td>
            </tr>
          </tbody>
        </table>
      </Card>

      <div className="grid2">
        <Card title="AR by Source & Type" sub={`Breakdown of ${fmtUSD(total)} outstanding`}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[
              { type: "Visit (eC)", amount: a.eclinpro_visit_ar, fill: "#0E7C7B" },
              { type: "Visit (CRIO)", amount: a.crio_visit_ar, fill: "#0E7C7B" },
              { type: "Inv (eC)", amount: a.eclinpro_invoicable_ar, fill: "#F05D23" },
              { type: "Inv (CRIO)", amount: a.crio_invoicable_ar, fill: "#F05D23" },
              { type: "Holdback", amount: a.holdback, fill: "#7C3AED" },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="type" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {[{ fill: "#0E7C7B" }, { fill: "#0E7C7B" }, { fill: "#F05D23" }, { fill: "#F05D23" }, { fill: "#7C3AED" }]
                  .map((c, i) => <Cell key={`cell-${i}`} fill={c.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="60-day AR Collection Forecast" sub="Due Date where set · else service +60 days" tag={fmtUSD(ar.ar_forecast_60d.reduce((s, r) => s + r.expected, 0))} tagColor="green">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ar.ar_forecast_60d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Bar dataKey="expected" radius={[4, 4, 0, 0]}>
                {ar.ar_forecast_60d.map((r, i) => <Cell key={`fc-${i}`} fill={r.type === "Visit" ? "#0E7C7B" : "#F05D23"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <ARTable
        title="CRIO Outstanding · Top Studies"
        count={`${fmtUSD(a.crio_ar)} · ${a.crio_items} items`}
        rows={crioFiltered}
        searchValue={crioQ}
        onSearch={setCrioQ}
        columns={[
          { key: "study", header: "Study", render: (r) => <strong>{r.study}</strong> },
          { key: "site", header: "Site", style: { color: "#6B7280", fontSize: 10 } },
          { key: "type", header: "Type", render: (r) => <span className={`sp ${r.type === "Autopay" ? "sp-visit" : "sp-inv"}`}>{r.type}</span> },
          { key: "amount", header: "Outstanding", className: "m r", render: (r) => fmtUSD(r.amount) },
          { key: "items", header: "Items", style: { color: "#6B7280", fontSize: 10 }, render: (r) => `${r.items} items` },
        ]}
      />

      <ARTable
        title="eClinPro Outstanding · By Sponsor"
        count={`${fmtUSD(a.eclinpro_ar)} · ${a.eclinpro_items} items`}
        rows={ecFiltered}
        searchValue={ecQ}
        onSearch={setEcQ}
        columns={[
          { key: "sponsor", header: "Sponsor", render: (r) => <strong>{r.sponsor}</strong> },
          { key: "study", header: "Study", style: { color: "#6B7280", fontSize: 10 } },
          { key: "amount", header: "Outstanding", className: "m r", render: (r) => fmtUSD(r.amount) },
          { key: "items", header: "Items", style: { color: "#6B7280", fontSize: 10 }, render: (r) => `${r.items} items` },
        ]}
      />
    </>
  );
}

/* ============================== Shared sub-components ============================== */
function KCard({ color, icon, label, value, valueColor, sub, pill, pillClass }) {
  return (
    <div className={`kcard ${color}`}>
      <div className={`kico ${color}`}>{icon}</div>
      <div className="klabel">{label}</div>
      <div className={`kval ${valueColor || ""}`}>{value}</div>
      {sub && <div className="ksub">{sub}</div>}
      {pill && <div className={`kpill ${pillClass || ""}`}>{pill}</div>}
    </div>
  );
}

function Card({ title, sub, tag, tagColor, full, children }) {
  return (
    <div className={`card ${full ? "full" : ""}`}>
      <div className="ch">
        <div>
          <div className="ct">{title}</div>
          {sub && <div className="cs">{sub}</div>}
        </div>
        {tag && <div className={`ctag ${tagColor || ""}`}>{tag}</div>}
      </div>
      <div className="cb">{children}</div>
    </div>
  );
}

function ARTable({ title, count, rows, columns, searchValue, onSearch }) {
  return (
    <div className="tcard">
      <div className="thead">
        <div><div className="ttitle">{title}</div><div className="tcount">{count}</div></div>
        <div className="relative" style={{ position: "relative" }}>
          <Search className="w-3.5 h-3.5" style={{ position: "absolute", left: 8, top: 9, color: "#9CA3AF" }} />
          <Input
            className="srch"
            style={{ paddingLeft: 28 }}
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            data-testid={`ri-table-search-${title.split(" ")[0].toLowerCase()}`}
          />
        </div>
      </div>
      <table>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} className={c.className}>{c.header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} style={{ textAlign: "center", padding: 24, color: "#6B7280" }}>No results</td></tr>}
          {rows.map((r, i) => (
            <tr key={`${r.study || r.sponsor}-${i}`}>
              {columns.map((c) => (
                <td key={c.key} className={c.className} style={c.style}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    below: { label: "Below", className: "sp-pend" },
    in_progress: { label: "In Progress", className: "", style: { background: "#FEF3C7", color: "#D97706" } },
    forecast: { label: "Forecast", className: "sp-proc" },
  };
  const s = map[status] || { label: status, className: "" };
  return <span className={`sp ${s.className}`} style={s.style}>{s.label}</span>;
}
